import { useState, ChangeEvent } from 'react';

import {
  Button,
  Grid,
  SelectChangeEvent,
  Paper,
  Divider,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';

import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';
import AlarmEditDialog from './AlarmEditDialog';
import AlarmList from './AlarmList';
import GreenButton from '../../components/GreenButton';
import AlarmModeSelector from './AlarmModeSelector';
import { SnapshotAlarm } from 'types';
import { grey } from '@mui/material/colors';

import { useAlarms } from 'features/alarms/api/getAlarms';
import { useCreateAlarm } from 'features/alarms/api/createAlarm';
import { useUpdateAlarm } from 'features/alarms/api/updateAlarm';
import { useDeleteAlarm } from 'features/alarms/api/deleteAlarm';
import AlarmSnapshotList from '../analyzers/AnalyzerSnapshotList';

const AlarmSettingsSetup = () => {
  const { data: alarms } = useAlarms({});
  const [selectedAlarmId, setSelectedAlarmId] = useState<number>(-1);
  const selectedAlarm = alarms?.find((alarm) => alarm.id === selectedAlarmId);

  const createAlarmMutation = useCreateAlarm({});
  const updateAlarmMutation = useUpdateAlarm({});
  const deleteAlarmMutation = useDeleteAlarm({});

  const [alarmEditDialogOpen, setAlarmEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleAlarmClicked = (alarm: SnapshotAlarm) => {
    setSelectedAlarmId(alarm.id);
  };

  const handleNewAlarmClicked = async () => {
    const newAlarm = {
      name: 'New Alarm',
      enabled: false,
      mode: 0,
    };

    await createAlarmMutation.mutateAsync({
      data: newAlarm,
    });
  };

  const handleAlarmUpdated = async (updatedAlarm: SnapshotAlarm) => {
    if (!alarms) return;
    setAlarmEditDialogOpen(false);
    await updateAlarmMutation.mutateAsync({
      data: updatedAlarm,
      alarmId: updatedAlarm.id,
    });
  };

  const handleAlarmModeChanged = (event: SelectChangeEvent<number>) => {
    if (!selectedAlarm) return;
    const { value } = event.target;
    const convertedMode = Number(value);
    if (convertedMode !== 0 && convertedMode !== 1) return;

    const updatedAlarm: SnapshotAlarm = {
      ...selectedAlarm,
      mode: convertedMode,
    };

    setSelectedAlarmId(updatedAlarm.id);
    handleAlarmUpdated(updatedAlarm);
  };

  const handleDeleteAlarmClicked = async () => {
    if (!selectedAlarm || !alarms) return;
    await deleteAlarmMutation.mutateAsync(selectedAlarm.id);
  };

  const handleAlarmChecked = async (
    event: ChangeEvent<HTMLInputElement>,
    updatedAlarm: SnapshotAlarm
  ) => {
    if (!alarms) return;
    const { checked } = event.target;
    const currentAlarm = alarms.find((alarm) => alarm.id === updatedAlarm.id);
    const checkedAlarm = { ...currentAlarm, enabled: checked };

    await updateAlarmMutation.mutateAsync({
      data: checkedAlarm,
      alarmId: checkedAlarm.id!,
    });
  };

  const handleStartChecked = async (
    event: ChangeEvent<HTMLInputElement>,
    alarmId: number | undefined
  ) => {
    if (!alarms || !alarmId || !selectedAlarm) return;
    const { checked } = event.target;

    const updatedAlarm = checked
      ? {
          ...selectedAlarm,
          start: checked,
          stop: !checked,
        }
      : {
          ...selectedAlarm,
          start: checked,
        };

    await updateAlarmMutation.mutateAsync({
      data: updatedAlarm,
      alarmId: selectedAlarm.id,
    });
  };

  const handleStopChecked = async (
    event: ChangeEvent<HTMLInputElement>,
    alarmId: number | undefined
  ) => {
    //This handles start/stop.
    if (!alarms || !alarmId || !selectedAlarm) return;
    const { checked } = event.target;
    const updatedAlarm = checked
      ? {
          ...selectedAlarm,
          stop: checked,
          start: !checked,
        }
      : {
          ...selectedAlarm,
          stop: checked,
        };

    await updateAlarmMutation.mutateAsync({
      data: updatedAlarm,
      alarmId: selectedAlarm.id,
    });
  };

  return (
    <>
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        itemToDelete={`Alarm ${selectedAlarm?.name}`}
        onSubmit={handleDeleteAlarmClicked}
        onClose={() => setDeleteDialogOpen(false)}
      />
      {selectedAlarm && (
        <AlarmEditDialog
          open={alarmEditDialogOpen}
          handleClose={() => setAlarmEditDialogOpen(false)}
          selectedAlarm={selectedAlarm}
          handleAlarmUpdated={handleAlarmUpdated}
        />
      )}
      <Grid
        container
        spacing={2}
        style={{
          paddingTop: '1.4rem',
          paddingBottom: '1.5rem',
          maxHeight: '85vh',
        }}
      >
        <Grid container item justifyContent='space-between'>
          <Grid container item xs spacing={1} style={{ paddingLeft: 0 }}>
            <Grid item xs={1} style={{ minWidth: '8rem' }}>
              <GreenButton
                name='New Alarm'
                onClick={handleNewAlarmClicked}
                fullWidth
              />
            </Grid>
            <Grid item xs={1} style={{ minWidth: '7rem' }}>
              <Button
                variant='contained'
                color='primary'
                fullWidth
                onClick={() => setAlarmEditDialogOpen(true)}
                disabled={!selectedAlarm}
              >
                Edit Alarm
              </Button>
            </Grid>
            <Grid item xs={1} style={{ minWidth: '8rem' }}>
              <Button
                variant='contained'
                color='error'
                fullWidth
                onClick={() => setDeleteDialogOpen(true)}
                disabled={!selectedAlarm}
              >
                Delete Alarm
              </Button>
            </Grid>
          </Grid>
          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!selectedAlarm?.start}
                  name='start-checkbox'
                  onChange={(event) => {
                    handleStartChecked(event, selectedAlarm?.id);
                  }}
                />
              }
              label='Alarm On Start'
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!selectedAlarm?.stop}
                  name='stop-checkbox'
                  onChange={(event) => {
                    handleStopChecked(event, selectedAlarm?.id);
                  }}
                />
              }
              label='Alarm On Start/Stop'
            />
          </Grid>
          <Grid item xs={2} style={{ minWidth: '7rem' }}>
            <AlarmModeSelector
              alarmMode={selectedAlarm?.mode}
              disabled={!selectedAlarm}
              handleAlarmModeChanged={handleAlarmModeChanged}
            />
          </Grid>
        </Grid>
        <Grid container item style={{ height: '100%' }}>
          <Grid
            container
            item
            xs={12}
            component={Paper}
            style={{ height: '100%' }}
          >
            <Grid item xs={6}>
              <AlarmList
                alarms={alarms}
                selectedAlarmId={selectedAlarm?.id}
                handleAlarmClicked={handleAlarmClicked}
                handleAlarmChecked={handleAlarmChecked}
              />
            </Grid>
            <Grid item>
              <Divider orientation='vertical' />
            </Grid>
            <Grid item xs style={{ height: '100%' }}>
              {selectedAlarm ? (
                <AlarmSnapshotList selectedAlarm={selectedAlarm} />
              ) : (
                <Typography color={grey[500]} sx={{ p: '2rem', pb: '15rem' }}>
                  Select an Alarm to associate it with snapshots.
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default AlarmSettingsSetup;
